import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { setSuccessHandler, markOwnSuccessPaths } from '../services/api';
import { CheckCircle2, AlertCircle, Info, X, Undo2 } from 'lucide-react';
import './Toast.css';

const ToastContext = createContext(null);

let _toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});
  // Отложенное общее подтверждение действия (см. ниже)
  const pendingRef = useRef(null);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
  }, []);

  const addToast = useCallback((message, type = 'info', options = {}) => {
    // Страница сказала своё — отменяем общее «Готово», чтобы не дублировать
    if (!options?._auto && pendingRef.current) {
      clearTimeout(pendingRef.current);
      pendingRef.current = null;
    }
    const id = ++_toastId;
    const optObj = typeof options === 'object' ? options : { duration: options };
    const onUndo = optObj.onUndo;
    // Если есть undo — даём 30 сек на отмену, иначе обычные 4 сек.
    const duration = optObj.duration || (onUndo ? 30000 : 4000);

    setToasts(prev => [...prev, { id, message, type, onUndo }]);

    timersRef.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      delete timersRef.current[id];
    }, duration);

    return id;
  }, []);

  const handleUndo = useCallback((toast) => {
    if (toast.onUndo) {
      toast.onUndo();
    }
    removeToast(toast.id);
  }, [removeToast]);

  // Любое изменяющее действие подтверждается коротким «Готово» — без этого
  // человек не понимает, сработало ли, и повторяет нажатие.
  //
  // Показываем с задержкой: если страница за это время скажет что-то своё
  // («Реквизиты сохранены», «ФБС включён: 12 из 12»), общее «Готово» отменяется —
  // два уведомления подряд на одно действие раздражают сильнее, чем их отсутствие.
  useEffect(() => {
    // Эти страницы показывают результат сами — полосой над списком
    markOwnSuccessPaths(['/products', '/placement', '/arrivals', '/marking', '/companies', '/setup', '/temp-workers']);  // мастер пишет итог шага сам
    setSuccessHandler((msg) => {
      if (pendingRef.current) clearTimeout(pendingRef.current);
      pendingRef.current = setTimeout(() => {
        pendingRef.current = null;
        addToast(msg, 'success', { duration: 2200, _auto: true });
      }, 700);
    });
    return () => {
      setSuccessHandler(null);
      if (pendingRef.current) clearTimeout(pendingRef.current);
    };
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span className="toast-icon">
              {t.type === 'success' && <CheckCircle2 size={16} />}
              {t.type === 'error' && <AlertCircle size={16} />}
              {t.type === 'info' && <Info size={16} />}
            </span>
            <span className="toast-msg">{t.message}</span>
            {t.onUndo && (
              <button className="toast-undo" onClick={() => handleUndo(t)}>
                <Undo2 size={14} />
                <span>Отмена</span>
              </button>
            )}
            <button className="toast-close" onClick={() => removeToast(t.id)}><X size={14} /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) return { addToast: () => {} };
  return ctx;
}
